import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { FAQItem } from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";

const faqs: FAQItem[] = [
  {
    question: "Can anxiety and depression happen at the same time?",
    answer:
      "Yes. Anxiety and depression frequently overlap, and many people experience symptoms of both. A comprehensive evaluation can help clarify the pattern and which treatment options may be appropriate.",
  },
  {
    question: "Can poor sleep make depression feel worse?",
    answer:
      "Poor sleep can affect energy, concentration, mood, and motivation, which may make depressive symptoms feel more intense. Sleep is one of many factors clinicians consider when evaluating the full picture.",
  },
  {
    question: "Why does my mind race when I try to sleep?",
    answer:
      "Racing thoughts at night are common with anxiety. When the body is exhausted but the mind stays activated, worrying and difficulty relaxing can make falling asleep harder. This does not by itself establish a diagnosis.",
  },
  {
    question: "Does having insomnia mean I have depression?",
    answer:
      "No. Insomnia can occur on its own or alongside anxiety, depression, or other conditions. An evaluation can help determine what may be contributing to sleep difficulties.",
  },
  {
    question: "Can TMS help everyone with depression?",
    answer:
      "No. TMS is an FDA-cleared treatment that may be appropriate for certain eligible patients after clinical evaluation. It is not appropriate for everyone, and candidacy is determined individually.",
  },
  {
    question: "What happens during a mental-health evaluation?",
    answer:
      "A clinician reviews your symptoms, sleep, anxiety, mood, treatment history, medications, medical history, and safety. The goal is to understand the full pattern before discussing treatment options.",
  },
  {
    question: "Should I stop my medication before TMS?",
    answer:
      "No. Psychiatric medication should not be stopped or changed without guidance from your prescribing healthcare provider. Medication decisions are clinical decisions made with your prescriber.",
  },
];

export default function DepressionAnxietyInsomnia() {
  return (
    <DepressionContentPage
      seo={{
        title: "Depression, Anxiety & Insomnia | Neuro Wellness",
        description:
          "Learn how anxiety, insomnia and depression can overlap and why understanding the full pattern of symptoms may guide treatment.",
        canonical: "/depression-anxiety-insomnia",
        keywords:
          "depression anxiety insomnia, anxiety and depression, racing thoughts, poor sleep and mood, mental health evaluation",
      }}
      badge="Mental Health Education"
      heroTitle="When Anxiety, Insomnia and Depression Overlap"
      heroSubtitle="Sometimes depression does not begin with sadness. Anxiety, racing thoughts and disrupted sleep can gradually affect energy, mood, concentration and everyday life."
      heroCtaPrimary="Check My Insurance Options"
      heroCtaPrimaryHref="/check-my-insurance-options"
      sections={[
        {
          heading: "It Can Be Difficult to Know Which Problem Came First",
          variant: "text",
          body: (
            <>
              <p>
                Anxiety can interfere with sleep. Poor sleep can contribute to
                exhaustion and difficulty concentrating. Prolonged distress can
                occur alongside depressive symptoms. For some people, these
                symptoms begin to reinforce one another, making it hard to tell
                where one problem ends and another begins.
              </p>
              <p>
                That does not mean one condition automatically causes another.
                But it does mean that looking at the whole pattern, rather than
                a single symptom, may help guide the conversation about what
                comes next.
              </p>
              <p>
                <Link
                  to="/depression-treatment-options"
                  className="text-accent font-semibold hover:underline underline-offset-2"
                >
                  Learn more about depression treatment options.
                </Link>
              </p>
            </>
          ),
        },
        {
          heading: "When Your Brain Won't Shut Off at Night",
          variant: "text",
          body: (
            <>
              <p>
                Many people describe lying in bed exhausted, yet unable to stop
                thinking. Racing thoughts, persistent worrying, and feeling
                physically tired but mentally alert can make it difficult to
                relax. Waking repeatedly through the night, or becoming anxious
                about not sleeping, can leave the next day feeling even harder.
              </p>
              <p>
                This experience is common, and it does not by itself mean
                something is wrong with you. But when it persists, it may be
                worth understanding more.
              </p>
              <p>
                <Link
                  to="/brain-wont-shut-off"
                  className="text-accent font-semibold hover:underline underline-offset-2"
                >
                  Learn more about racing thoughts and sleep.
                </Link>
              </p>
            </>
          ),
        },
        {
          heading: "How Poor Sleep Can Affect the Rest of Your Life",
          variant: "cards",
          cards: [
            {
              title: "Energy & Motivation",
              body: "Poor sleep can reduce energy and make everyday tasks feel heavier than they used to.",
            },
            {
              title: "Concentration",
              body: "Sleep disruption may affect focus, memory, and the ability to stay engaged at work or home.",
            },
            {
              title: "Mood & Irritability",
              body: "Exhaustion can lower emotional resilience, making irritability and low mood more noticeable.",
            },
            {
              title: "Social Activity",
              body: "Some people withdraw from friends or activities when sleep and mood are affected.",
            },
            {
              title: "Appetite & Enjoyment",
              body: "Changes in sleep may accompany changes in appetite and a reduced sense of enjoyment.",
            },
            {
              title: "Work & Family",
              body: "When functioning changes, the effects can ripple into work performance and family relationships.",
            },
          ],
        },
        {
          heading: "Why a Comprehensive Evaluation Matters",
          variant: "text",
          body: (
            <>
              <p>
                A clinician may consider when symptoms started, sleep patterns,
                anxiety, depression symptoms, panic, trauma history, medication
                history, previous therapy, medical history, safety, and other
                potential diagnoses.
              </p>
              <p className="font-semibold text-primary">
                The goal is not simply to identify one symptom. It is to
                understand the pattern.
              </p>
            </>
          ),
        },
        {
          heading: "Where TMS May Fit",
          variant: "text",
          body: (
            <>
              <p>
                TMS is an FDA-cleared, non-invasive treatment used for certain
                psychiatric conditions. For eligible patients, it may be
                considered after a clinical evaluation. TMS is not a treatment
                for insomnia itself, and whether it is appropriate depends on
                the full clinical picture.
              </p>
              <p>
                <Link
                  to="/services/tms"
                  className="text-accent font-semibold hover:underline underline-offset-2"
                >
                  Learn more about TMS therapy.
                </Link>
              </p>
            </>
          ),
        },
      ]}
      faq={faqs}
      faqHeading="Frequently Asked Questions"
      relatedLinks={[
        {
          title: "My Brain Won't Shut Off at Night",
          href: "/brain-wont-shut-off",
        },
        {
          title: "When a Panic Attack Changes Everything",
          href: "/panic-anxiety-depression",
        },
        {
          title: "I Never Had Depression Before. Why Now?",
          href: "/sudden-depression",
        },
        {
          title: "Depression Treatment Options",
          href: "/depression-treatment-options",
        },
        { title: "TMS Therapy", href: "/services/tms" },
        {
          title: "Explore More Mental Health Education",
          href: "/mental-health-resources",
        },
      ]}
      finalCtaHeading="You Don't Have to Figure Out Which Symptom Came First"
      finalCtaText="A clinical evaluation can help organize what you are experiencing and determine which treatment options may be appropriate."
      finalCtaPrimaryLabel="Check My Insurance Options"
      finalCtaPrimaryHref="/check-my-insurance-options"
      finalCtaSecondaryLabel="Schedule a Private Consultation"
      finalCtaSecondaryHref="/contactus"
    />
  );
}
